import { type ApiSchemas, call } from '@rental/contracts/client'
import { STAFF_LOCALES } from '@rental/domain'
import { Button, Field, Notice } from '@rental/ui'
import { type FormEvent, useEffect, useId, useRef, useState } from 'react'
import { useAccess } from '../access-context'
import { api } from '../api'
import { ChoiceChips } from '../components/ChoiceChips'
import { OWNER_CODE, OwnerCodeField } from '../components/OwnerCodeField'
import { ReasonList, SubPanel } from '../components/SubPanel'
import { Facts, FIELD, ProblemNotice, StaffInput } from '../components/ui'
import { noImplicitSubmit } from '../forms'
import { useLocale } from '../i18n'
import type { StaffProblem } from '../problems'
import { draftOf, keyChange, type Person, personChanges, type RoleOption } from './people'

/** How an edit ended, for the person's page to say. */
export type EditOutcome =
  | {
      kind: 'saved'
      person: Person
      /** A move to a role that signs in with a code: its new key, shown once. */
      authenticator: ApiSchemas['AuthenticatorKey'] | null
    }
  /** Someone changed this person meanwhile: nothing was saved, and the page shows them as they are now. */
  | { kind: 'conflict' }

/**
 * S9: a person's name, e-mail language and role. The form starts from the
 * person as read when it opened, and sends only what was changed, with that
 * version: a person changed meanwhile is refused, and never overwritten with
 * what this form was showing. A new role is access given or taken, so it
 * takes the Owner's code, typed again after any answer; nobody changes their
 * own role, and a deactivated person's role changes after the reactivation.
 */
export function EditPerson({
  person,
  roles,
  onDone,
  onCancel,
}: {
  person: Person
  roles: readonly RoleOption[]
  onDone: (outcome: EditOutcome) => void
  onCancel: () => void
}) {
  const { messages: m } = useLocale()
  const copy = m.person
  const { takeOver } = useAccess()
  const titleId = useId()
  const reasonsId = useId()
  const firstField = useRef<HTMLInputElement>(null)
  const [read] = useState(person)
  const [draft, setDraft] = useState(() => draftOf(person))
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<StaffProblem | null>(null)

  // The form takes the place of the button that opened it: the focus goes to its first field.
  useEffect(() => {
    firstField.current?.focus()
  }, [])

  const changes = personChanges(read, draft)
  const roleChanged = changes.role !== undefined
  const key = keyChange(roles, read.role, draft.role)
  const roleFixed = read.self ? copy.roleSelf : read.disabledAt !== null ? copy.roleDisabled : null
  const reasons = busy
    ? []
    : [
        ...(draft.displayName.trim() === '' ? [copy.why.name] : []),
        ...(Object.keys(changes).length === 0 ? [copy.why.nothing] : []),
        ...(roleChanged && !OWNER_CODE.test(code) ? [copy.why.code] : []),
      ]
  const roleName = (role: string) => (m.roles as Readonly<Record<string, string>>)[role] ?? role

  async function save() {
    if (busy || reasons.length > 0) return
    setProblem(null)
    setBusy(true)
    const result = await call(
      api.PATCH('/v1/staff/people/{userId}', {
        params: { path: { userId: read.id } },
        body: { version: read.version, ...changes, ...(roleChanged ? { code } : {}) },
      }),
    )
    setBusy(false)
    // A code is spent whether or not the change went through: a new one is typed either way.
    setCode('')
    if (result.ok) {
      onDone({ kind: 'saved', ...result.data })
      return
    }
    if (takeOver(result.problem)) return
    if (result.problem.kind === 'api' && result.problem.code === 'version_conflict') {
      onDone({ kind: 'conflict' })
      return
    }
    setProblem(result.problem)
  }

  return (
    <form
      onSubmit={(event: FormEvent) => {
        event.preventDefault()
        void save()
      }}
      onKeyDown={noImplicitSubmit}
      aria-labelledby={titleId}
      noValidate
    >
      <SubPanel title={<span id={titleId}>{copy.edit}</span>}>
        <Field className={FIELD} label={copy.name} hint={copy.nameHint} required>
          {(control) => (
            <StaffInput
              {...control}
              ref={firstField}
              autoComplete="off"
              maxLength={60}
              value={draft.displayName}
              onChange={(event) => setDraft({ ...draft, displayName: event.target.value })}
            />
          )}
        </Field>
        <ChoiceChips
          name="person-locale"
          label={copy.locale}
          options={STAFF_LOCALES.map((value) => ({ value, label: m.languages[value] }))}
          value={draft.locale}
          onChange={(locale) => setDraft({ ...draft, locale })}
        />
        <p className="text-xs text-text-muted">{copy.localeHint}</p>
        {roleFixed ? (
          <>
            <Facts items={[[copy.role, roleName(read.role)]]} />
            <p className="text-sm text-text-muted">{roleFixed}</p>
          </>
        ) : (
          <ChoiceChips
            name="person-role"
            label={copy.role}
            options={roles.map((role) => ({ value: role.code, label: roleName(role.code) }))}
            value={draft.role}
            onChange={(role) => setDraft({ ...draft, role })}
          />
        )}
        {key === 'newKey' ? (
          <Notice tone="info">
            <p>{copy.toCodeRole}</p>
          </Notice>
        ) : key === 'keyEnds' ? (
          <Notice tone="warning">
            <p>{copy.fromCodeRole}</p>
          </Notice>
        ) : null}
        {roleChanged ? (
          <OwnerCodeField className={FIELD} label={copy.code} value={code} onChange={setCode} />
        ) : null}
        <ProblemNotice problem={problem} />
        <div className="flex flex-wrap gap-3">
          <Button
            type="submit"
            variant="staff"
            busy={busy}
            disabled={reasons.length > 0}
            aria-describedby={reasons.length > 0 ? reasonsId : undefined}
          >
            {copy.save}
          </Button>
          <Button variant="outline" className="rounded-sm" disabled={busy} onClick={onCancel}>
            {copy.cancel}
          </Button>
        </div>
        <ReasonList id={reasonsId} reasons={reasons} />
      </SubPanel>
    </form>
  )
}
